import CarDetailDialog from '@components/CarDetailDialog';
import { Button, makeStyles, Paper } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from '@tanstack/react-table';
import { isFunction } from 'lodash';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import useRole from 'src/hooks/useRole';
import { useValuationSettings } from 'src/hooks/useValuationSettings';
import { ValuationCountryCode } from 'src/modules/generated/api';
import UserRole from 'src/types/UserRoles';

import CarDetailHeadline from './CarDetailHeadline';
import { LeadIconCell } from './cars-table-next/cells/LeadIconCell';
import { TextCellFallback } from './cars-table-next/cells/TextCell';
import { columns } from './cars-table-next/columns/columns';
import { relevantTaxes } from './cars-table-next/columns/constants';
import { defaultColumn } from './cars-table-next/columns/default-column';
import { rowSize } from './cars-table-next/constants';
import {
  compareCellValuesForSort,
  getCellConditionalFormatColors,
  getConditionalFormatStyles,
  getConditionalRowFormatStyles,
  resolveCellBackgroundStyles,
} from './cars-table-next/lib';
import { columnCarsVisibilityState, columnOrderState } from './cars-table-next/state';
import { TableCell } from './cars-table-next/table/TableCell';
import { TableHeaderCell } from './cars-table-next/table/TableHeaderCell';
import { CarsTableNextItem, StrictColumnDef } from './cars-table-next/types';
import { useConditionalFormats } from './cars-table-next/useConditionalFormats';
import ImageOverlay from './ui/ImageOverlay';

const MAX_VISIBLE_ROWS = 7;

// Columns redundant at package level (all cars share source/dates; ratings are personal)
const EXCLUDED_COLUMNS = new Set([
  'rating',
  'source',
  'dateStart',
  'dateEnd',
  'marketingChannel',
  'numAuctions',
  'onlineTimeInMinutes',
  'taxType',
  'firstCallDealerAssignedKvps',
  'firstCallDealer',
]);

const useStyles = makeStyles((theme) => ({
  headingBox: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${grey[200]}`,
  },
  tableScroll: {
    overflowX: 'auto',
  },
  headerRow: {
    display: 'flex',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: '#fff',
  },
  bodyRow: {
    display: 'flex',
    cursor: 'pointer',
    '&:hover': {
      filter: 'brightness(0.97)',
    },
  },
}));

type PackageCarsListProps = {
  cars?: CarsTableNextItem[];
  title: string;
};

const PackageCarsList = ({ cars = [], title }: PackageCarsListProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: conditionalFormats = [] } = useConditionalFormats();
  const savedColumnVisibility = useRecoilValue(columnCarsVisibilityState);
  const savedColumnOrder = useRecoilValue(columnOrderState);
  const { hasRole } = useRole();
  const { valuationCountry } = useValuationSettings();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [detailIndex, setDetailIndex] = useState<number | undefined>();
  const [isExpanded, setIsExpanded] = useState(false);

  const tableColumns = useMemo<StrictColumnDef<CarsTableNextItem>[]>(() => {
    const all = columns()
      .filter(
        (col) =>
          col.meta?.roles === undefined ||
          hasRole(UserRole.ADMIN) ||
          col.meta.roles.every((role) => hasRole(role as any) || relevantTaxes[valuationCountry!]?.includes(role)),
      )
      .filter((col) => {
        if (
          valuationCountry === ValuationCountryCode.De &&
          (col.id === 'marketPriceDE' || col.id === 'referenceVehiclesDE')
        )
          return false;
        if (col.id === 'marketPriceCZ' || col.id === 'referenceVehiclesCZ' || col.id?.startsWith('potential.CZ.'))
          return valuationCountry === ValuationCountryCode.Sk;
        if (col.id === 'marketPriceSK' || col.id === 'referenceVehiclesSK' || col.id?.startsWith('potential.SK.'))
          return valuationCountry === ValuationCountryCode.Cz;
        return true;
      });
    const filtered: StrictColumnDef<CarsTableNextItem>[] = all
      .filter((col) => !EXCLUDED_COLUMNS.has(col.id))
      .map((col) => {
        const base = {
          ...col,
          meta: col.meta ? { ...col.meta, filter: undefined } : col.meta,
          sortingFn: compareCellValuesForSort,
        };
        if (col.id !== 'car') return base;
        return {
          ...base,
          cell: ({ row: { original } }) => (
            <LeadIconCell
              text={original.model}
              supportText={original.vin}
              recommended={original.recommended}
              icon={
                <ImageOverlay
                  src={original.imageThumbnailLink ?? '/images/icons/car.svg'}
                  icon={() => null as any}
                  height={40}
                  disableOverlay
                />
              }
            />
          ),
          meta: {
            ...base.meta,
            fallback: () => <TextCellFallback textWidth="80%" supportTextWidth="50%" withStartAdornment />,
          },
        };
      });
    const orderMap = new Map(savedColumnOrder.map((id, idx) => [id, idx]));
    return [...filtered].sort((a, b) => (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity));
  }, [savedColumnOrder, hasRole, valuationCountry]);

  const columnVisibility = useMemo(
    () =>
      Object.fromEntries(
        tableColumns.map((col) => [col.id, savedColumnVisibility[col.id] ?? col.meta?.columnVisible !== false]),
      ),
    [tableColumns, savedColumnVisibility],
  );

  const carsData = useMemo(() => cars.map((car) => ({ ...car, rowId: car.carId })), [cars]);

  const table = useReactTable({
    data: carsData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (car, index) => car.rowId || car.carId || String(index),
    defaultColumn,
    columnResizeMode: 'onChange',
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
  });

  const headerGroup = table.getHeaderGroups()[0];
  const { rows } = table.getRowModel();
  const selectedCar = detailIndex !== undefined ? carsData[detailIndex] : undefined;

  return (
    <>
      <Paper elevation={3}>
        <div className={classes.headingBox}>
          <CarDetailHeadline title={title} />
        </div>
        <div
          className={classes.tableScroll}
          style={
            cars.length > MAX_VISIBLE_ROWS && !isExpanded
              ? { maxHeight: MAX_VISIBLE_ROWS * rowSize + rowSize, overflowY: 'auto' }
              : undefined
          }
        >
          {/* Header */}
          <div className={classes.headerRow}>
            {headerGroup.headers.map((header) => {
              const { align, isNumeric } = header.column.columnDef.meta ?? {};
              return (
                <TableHeaderCell
                  key={header.id}
                  style={{ position: 'relative', width: header.getSize(), height: rowSize, flexShrink: 0 }}
                  align={align ?? (isNumeric ? 'right' : undefined)}
                  menuReducer={() => {}}
                  actions={[]}
                  resizeHandleProps={{
                    onMouseDown: header.getResizeHandler(),
                    onTouchStart: header.getResizeHandler(),
                  }}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHeaderCell>
              );
            })}
          </div>

          {/* Body */}
          <div>
            {rows.map((row, index) => {
              const rowBgStyles = getConditionalRowFormatStyles(row, conditionalFormats);
              const rowBg =
                typeof (rowBgStyles?.backgroundImage ?? rowBgStyles?.background) === 'string'
                  ? ((rowBgStyles?.backgroundImage ?? rowBgStyles?.background) as string)
                  : undefined;
              return (
                <div
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  className={classes.bodyRow}
                  style={{ '--row-background': rowBg ?? '' } as any}
                  onClick={() => setDetailIndex(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setDetailIndex(index);
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const cellProps = isFunction(cell.column.columnDef.meta?.tableCellProps)
                      ? cell.column.columnDef.meta?.tableCellProps(cell)
                      : cell.column.columnDef.meta?.tableCellProps;
                    const cellFormatStyles = getConditionalFormatStyles(cell, conditionalFormats);
                    const cellColors = getCellConditionalFormatColors(cell, conditionalFormats);
                    return (
                      <TableCell
                        key={cell.id}
                        {...cellProps}
                        style={{
                          ...cellProps?.style,
                          ...cellFormatStyles,
                          ...resolveCellBackgroundStyles(rowBg, cellColors),
                          position: 'relative',
                          width: cell.column.getSize(),
                          height: rowSize,
                          flexShrink: 0,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        {cars.length > MAX_VISIBLE_ROWS && (
          <Button
            variant="text"
            startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setIsExpanded((v) => !v)}
          >
            {isExpanded ? t('common.showLess') : t('common.showFullTable')}
          </Button>
        )}
      </Paper>

      {selectedCar && (
        <CarDetailDialog
          car={selectedCar}
          index={detailIndex}
          maxCars={carsData.length}
          moveToNext={
            detailIndex !== undefined && detailIndex < carsData.length - 1
              ? () => setDetailIndex((i) => (i !== undefined ? i + 1 : undefined))
              : undefined
          }
          moveToPrev={
            detailIndex !== undefined && detailIndex > 0
              ? () => setDetailIndex((i) => (i !== undefined ? i - 1 : undefined))
              : undefined
          }
          onClose={() => setDetailIndex(undefined)}
          onEdit={() => Promise.resolve()}
        />
      )}
    </>
  );
};

export default memo(PackageCarsList);
